import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import urlParse from 'url-parse';
import qs from 'querystringify';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  ExternalLink,
  Page,
  Text,
  TextEditor,
  ToolTip,
} from '../../components';
import BenefitCategory from './benefits/BenefitCategory';
import BenefitCategoryRedesign from './benefits/BenefitCategoryRedesign';
import {
  getIFrameId,
  getTheme,
  getUserDetails,
  getFeatures,
  getSelectedLanguage,
  getIsRedesign,
} from '../../modules/selectors';
import SettingToggle from '../../components/SettingToggle';
import api from '../../utils/services';
import sendHeightPostMessage from '../../utils/sendHeightPostMessage';
import { WindowEventListener } from '../../listeners';
import { Legend } from './benefits/BenefitTable';
import { LegendRedesign } from './benefits/BenefitTableRedesign';
import { Provider } from './Context';
import { Languages } from '../../utils/constants';


const propTypes = {
  iFrameId: PropTypes.string,
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      planId: PropTypes.string.isRequired,
      carrierId: PropTypes.string.isRequired,
    }).isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  user: PropTypes.shape({
    agentNpn: PropTypes.string,
    token: PropTypes.string,
    userId: PropTypes.string,
    userRole: PropTypes.string,
  }),
  // Listen for a theme change since it may change the components height
  theme: PropTypes.shape({}),
  features: PropTypes.shape({
    liquidTemplates: PropTypes.bool,
    richText: PropTypes.bool,
    staticHtml: PropTypes.bool,
  }).isRequired,
  selectedLanguage: PropTypes.shape({
    languageId: PropTypes.number,
  }).isRequired,
  isRedesignFromState: PropTypes.bool,
};
const defaultProps = {
  iFrameId: null,
  theme: null,
  user: null,
  isRedesignFromState: false,
};

// Note: This also allows the plan to be edited if the user is logged in
class ViewPlan extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editBenefitValue: null,
      editCategoryName: null,
      editSubCategoryName: null,
      editBenefitName: null,
      addBenefit: null,
      enableReorder: false,
      isDragged: false,
      loading: true,
      plan: {},
    };
  }

  componentDidMount() {
    const { match: { params: { planId } }, location: { search } } = this.props;
    const { query } = urlParse(search);
    let { languageId, page } = qs.parse(query);
    if (!languageId) {
      languageId = Languages.en_US;
    }
    if (!page) {
      page = 1;
    }
    api.getPlanByIdAndLanguage(planId, qs.stringify({ languageId }, true))
      .then((data) => {
        this.setState({ plan: data });
      }).finally(() => {
        this.setState({ loading: false, page });
      });

    this.sendHeightUpdate();
  }

  componentDidUpdate() {
    this.sendHeightUpdate();
  }

  sendHeightUpdate = () => {
    const { iFrameId } = this.props;
    sendHeightPostMessage(iFrameId);
  }

  handleUpdateBenefitOrder = (updatedPlanBenefits) => {
    this.setState((state) => {
      const planBenefits = [];
      state.plan.planBenefits.forEach((planBenefit) => {
        let hasPlanBenefit = false;
        updatedPlanBenefits.forEach((updatedBenefit) => {
          if (planBenefit.id === updatedBenefit.id) {
            planBenefits.push({ ...planBenefit, order: updatedBenefit.order });
            hasPlanBenefit = true;
          }
        });
        if (!hasPlanBenefit) {
          planBenefits.push({ ...planBenefit });
        }
      });
      return {
        isDragged: true,
        plan: {
          ...state.plan,
          planBenefits,
        },
      };
    });
  }

  handleUpdateBulkPlanBenefits = (updatedPlanBenefits) => {
    api.updateBatchPlanBenefits(updatedPlanBenefits)
      .then((data) => {
        this.setState(state => ({
          plan: {
            ...state.plan,
            planBenefits: data,
          },
          editBenefitName: null,
          editCategoryName: null,
          editSubCategoryName: null,
          editBenefitValue: null,
          isDragged: false,
        }));
      });
  }

  handleStartEdit = (event, categoryName, addBenefit) => {
    // When using this function make sure to pass the name and value,
    // where name should be defined in the state.
    const {
      editBenefitValue,
      editCategoryName,
      editBenefitName,
      editSubCategoryName,
    } = this.state;

    const { currentTarget: { attributes: { name: nameObject, value: valueObject } } } = event;

    const name = nameObject ? nameObject.value : undefined;
    const value = valueObject ? valueObject.value : undefined;

    // Clicking again on the text editor, makes the name and value undefined.
    if (!name) {
      return;
    }
    // This initialization is to ensure to only open a single text box at a time.
    const initializationObject = {
      editBenefitValue: null,
      editCategoryName: null,
      editBenefitName: null,
      editSubCategoryName: null,
      addBenefit: null,
    };
    delete initializationObject[name];
    this.setState({ ...initializationObject });

    // Prevent text reset on clicking the same field
    if (value === editBenefitValue
       || value === editCategoryName
       || value === editBenefitName
       || value === editSubCategoryName) {
      return;
    }


    if (name === 'editBenefitValue') {
      if (value === undefined) {
        this.setState({ [name]: { BenefitId: value } });
      } else {
        this.setState({ [name]: { BenefitId: parseInt(value, 10) } });
      }
    } else if (addBenefit) {
      this.setState({ [name]: addBenefit });
    } else {
      // Category name helps in editing subcategory name and benefit name.
      this.setState({ [name]: { value, categoryName } });
    }
  }

  handleSaveEdit = (value, categoryName, isNewSubCategory) => {
    // categoryName is used to edit the subcategory and benefit name.
    const {
      editBenefitValue,
      editCategoryName,
      editBenefitName,
      editSubCategoryName,
    } = this.state;
    // Edit by benefit ID.
    if (editBenefitValue) {
      this.editBenefitValue(value);
      // Edit by Category name.
    } else if (editCategoryName) {
      this.editCategoryName(value);
      // Edit by Benefit Name.
    } else if (editBenefitName) {
      this.editBenefitName(value, categoryName);
      // Edit by Sub Category Name.
    } else if (editSubCategoryName) {
      this.editSubCategoryName(value, categoryName, isNewSubCategory);
    }
  }

  editBenefitName = (newBenefitName, categoryName) => {
    const { plan: { planBenefits }, editBenefitName } = this.state;
    let isBenefitExist = false;
    planBenefits.forEach((benefit, index) => {
      if (benefit.category === categoryName
        && benefit.name === editBenefitName.value
        && benefit.id !== undefined) {
        planBenefits[index].name = newBenefitName;
        isBenefitExist = true;
      }
    });
    // if the benefit name doesn't exist in the list, add the new benefit.
    if (!isBenefitExist) {
      this.addBenefitByName(newBenefitName, categoryName);
    } else {
      this.handleUpdateBulkPlanBenefits(planBenefits);
    }
  }

  editCategoryName = (newCategoryName) => {
    const { plan: { planBenefits }, editCategoryName } = this.state;
    const isBenefitExists = planBenefits
      .some(pb => pb.category === editCategoryName.value && pb.id !== undefined);
    if (!isBenefitExists) {
      this.addBenefitByCategory(newCategoryName);
    } else {
      planBenefits.forEach((benefit, index) => {
        if (benefit.category === editCategoryName.value) {
          planBenefits[index].category = newCategoryName;
        }
      });
      this.handleUpdateBulkPlanBenefits(planBenefits);
    }
  }

  editBenefitValue = (newBenefitValue) => {
    const { plan: { planBenefits }, editBenefitValue } = this.state;
    const { selectedLanguage: { languageId } } = this.props;
    if (editBenefitValue && editBenefitValue.BenefitId === undefined) {
      this.addBenefitByValue(newBenefitValue);
    } else {
      api.updatePlanBenefit(editBenefitValue.BenefitId, { value: newBenefitValue, languageId })
        .then(() => {
          const updatedBenefit = planBenefits.find(b => b.id === editBenefitValue.BenefitId);
          updatedBenefit.value = newBenefitValue;
          this.setState({ editBenefitValue: null });
        });
    }
  }

  editSubCategoryName = (newSubCategoryName, categoryName, isNewSubCategory) => {
    const { plan: { planBenefits }, editSubCategoryName } = this.state;
    if (isNewSubCategory) {
      this.addBenefitBySubCategory(newSubCategoryName, categoryName);
    } else {
      planBenefits.forEach((benefit, index) => {
        if (benefit.category === categoryName
          && benefit.subcategory === editSubCategoryName.value) {
          planBenefits[index].subcategory = newSubCategoryName;
        }
      });
      this.handleUpdateBulkPlanBenefits(planBenefits);
    }
  }

  addBenefitByValue = (newBenefitValue) => {
    const { plan: { id: planId } } = this.state;
    let order = this.getMaxOrderValueByCategory();
    const { selectedLanguage: { languageId } } = this.props;
    const createBody = {
      planId,
      name: '',
      category: '',
      subCategory: '',
      value: newBenefitValue,
      order: order += 1,
      languageId,
    };
    api.addPlanBenefit(createBody)
      .then((data) => {
        this.setState(state => ({
          ...state,
          plan: {
            ...state.plan,
            planBenefits: state.plan.planBenefits.map((planBenefit) => {
              if (planBenefit.id === undefined) {
                return data;
              }
              return planBenefit;
            }),
          },
        }));
      });
  }

  addBenefitByName = (newBenefitName, categoryName) => {
    const { plan: { planBenefits } } = this.state;
    const emptyBenefits = planBenefits.filter(pb => pb.category === categoryName && pb.value === '' && pb.id === undefined);
    let order = this.getMaxOrderValueByCategory(categoryName);
    const { selectedLanguage: { languageId } } = this.props;
    emptyBenefits.forEach((benefit) => {
      const createBody = {
        planId: benefit.planId,
        name: newBenefitName,
        category: benefit.category,
        subcategory: benefit.subcategory,
        value: '',
        order: order += 1,
        languageId,
      };
      api.addPlanBenefit(createBody)
        .then((data) => {
          this.setState(state => ({
            ...state,
            plan: {
              ...state.plan,
              planBenefits: state.plan.planBenefits.map((planBenefit) => {
                if (planBenefit.subcategory === data.subcategory && planBenefit.category === data.category && planBenefit.value === '' && planBenefit.id === undefined) {
                  return data;
                }
                return planBenefit;
              }),
            },
            benefitName: null,
          }));
        });
    });
  }

  addBenefitByCategory = (categoryName) => {
    const { plan: { id: planId } } = this.state;
    let order = this.getMaxOrderValueByCategory();
    const { selectedLanguage: { languageId } } = this.props;
    const createBody = {
      planId,
      name: '',
      category: categoryName,
      subCategory: '',
      value: '',
      order: order += 1,
      languageId,
    };
    api.addPlanBenefit(createBody)
      .then((data) => {
        this.setState(state => ({
          ...state,
          plan: {
            ...state.plan,
            planBenefits: state.plan.planBenefits.map((planBenefit) => {
              if (planBenefit.id === undefined) {
                return data;
              }
              return planBenefit;
            }),
          },
        }));
      });
  }

  addBenefitBySubCategory = (newSubCategoryName, categoryName) => {
    const { plan: { planBenefits, id: planId } } = this.state;
    let order = this.getMaxOrderValueByCategory(categoryName);
    const emptyColumnBenefit = planBenefits
      .filter(planBenefit => planBenefit.category === categoryName && planBenefit.subcategory === '' && planBenefit.id === undefined)[0];
    const { selectedLanguage: { languageId } } = this.props;
    const createBody = {
      planId,
      name: emptyColumnBenefit.name,
      value: emptyColumnBenefit.value,
      category: emptyColumnBenefit.category,
      subcategory: newSubCategoryName,
      order: order += 1,
      languageId,
    };
    api.addPlanBenefit(createBody)
      .then((data) => {
        this.setState(state => ({
          ...state,
          plan: {
            ...state.plan,
            planBenefits: state.plan.planBenefits.map((planBenefit) => {
              if (planBenefit.category === data.category && planBenefit.value === '' && planBenefit.id === undefined && planBenefit.subcategory === '') {
                return data;
              }
              return planBenefit;
            }),
          },
          editSubCategoryName: null,
        }));
      });
  }

  handleAddPlanBenefit = (value) => {
    const {
      plan: { id: planId, planBenefits },
      addBenefit: { category, subCategoryName: subCategory, planBenefitName: name },
    } = this.state;
    let order = this.getMaxOrderValueByCategory(category);
    const { selectedLanguage: { languageId } } = this.props;
    // to add a new benefit to the details table.
    const createBody = {
      planId, name, value, category, subCategory, order: order += 1, languageId,
    };
    api.addPlanBenefit(createBody)
      .then((data) => {
        // if there is a plan benefit in the state, just update it otherwise add a new one.
        const isPlanBenefitInState = planBenefits.some(pb => pb.id === undefined);
        if (isPlanBenefitInState) {
          this.setState(state => ({
            ...state,
            plan: {
              ...state.plan,
              planBenefits: state.plan.planBenefits.map((planBenefit) => {
                if (planBenefit.id === undefined) {
                  return data;
                }
                return planBenefit;
              }),
            },
            addBenefit: null,
          }));
        } else {
          planBenefits.push(data);
          this.setState({ addBenefit: null });
        }
      });
  }

  getMaxOrderValueByCategory = (category) => {
    const { plan: { planBenefits } } = this.state;
    const listOfOrderValues = planBenefits
      .filter((planBenefit) => {
        // if no category is passed, return the max order value of all the plan benefits.
        if (!category) {
          return planBenefit.order !== undefined;
        }
        return planBenefit.category === category && planBenefit.order !== undefined;
      })
      .map(planBenefit => planBenefit.order);

    return listOfOrderValues.length > 0 ? Math.max(...listOfOrderValues) : 0;
  }

  handleCancelEdit = () => {
    this.setState({
      editBenefitValue: null,
      editCategoryName: null,
      editBenefitName: null,
      editSubCategoryName: null,
      addBenefit: null,
    });
  }

  groupBy = (objectArray, property) => objectArray.reduce((acc, obj) => {
    const key = obj[property];
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {})

  onDragEnd = (result) => {
    const { source, destination, type } = result;
    const { plan: { planBenefits } } = this.state;
    if (!destination) {
      return;
    }
    // return when dropped in the same location.
    if (destination.droppableId === source.droppableId
      && destination.index === source.index) {
      return;
    }
    if (type === 'category') {
      // code to handle ordering the categories.
      // Group the plan benefits based on categories and get the value's of those.
      const groupByCategory = Object.values(this.groupBy(planBenefits, 'category'));
      const tempBuffer = groupByCategory.splice(source.index, 1);
      groupByCategory.splice(destination.index, 0, tempBuffer[0]);
      const updatedOrderForPlanBenefits = [];
      let counter = -1;
      groupByCategory.forEach((benefit) => {
        benefit.forEach((pb) => {
          updatedOrderForPlanBenefits.push({ ...pb, order: counter += 1 });
        });
      });
      this.handleUpdateBenefitOrder(updatedOrderForPlanBenefits);
    } else if (type === 'subcategory') {
      const { droppableId } = source;
      // split the string on the first occurance of whitespace.
      const categoryName = droppableId.match(/^(\S+)\s(.*)/).slice(1)[1];
      const benefitsByCategoryName = planBenefits.filter(b => b.category === categoryName);
      const subcategoryNames = [...new Set(benefitsByCategoryName.map(b => b.subcategory))];

      const sourceBenefitWithMinimumOrderValue = benefitsByCategoryName
        .filter(b => b.subcategory === subcategoryNames[source.index])
        .map(b => b.order);
      const destinationBenefitWithMinimumOrderValue = benefitsByCategoryName
        .filter(b => b.subcategory === subcategoryNames[destination.index])
        .map(b => b.order);

      benefitsByCategoryName
        .filter(b => b.subcategory === subcategoryNames[destination.index]).forEach((pb, index) => {
          const planBenefit = pb;
          if (sourceBenefitWithMinimumOrderValue[index] !== undefined) {
            planBenefit.order = sourceBenefitWithMinimumOrderValue[index];
          }
        });
      benefitsByCategoryName
        .filter(b => b.subcategory === subcategoryNames[source.index]).forEach((pb, index) => {
          const planBenefit = pb;
          if (destinationBenefitWithMinimumOrderValue[index] !== undefined) {
            planBenefit.order = destinationBenefitWithMinimumOrderValue[index];
          }
        });

      this.handleUpdateBenefitOrder(
        benefitsByCategoryName,
      );
    } else {
      // code to handle ordering each plan benefit inside a category.
      // in this block the type will the category name.
      const categoryBenefits = planBenefits.filter(b => b.category === type);
      const initialSubCategory = [...new Set(categoryBenefits.map(b => b.subcategory))][0];
      const uniqueBenefitNames = [...new Set(categoryBenefits.map(b => b.name))];
      const modifiedCategoryBenefits = [];
      uniqueBenefitNames.forEach((benefitName) => {
        modifiedCategoryBenefits.push([...categoryBenefits.filter(b => b.name === benefitName)]);
      });
      const updatedOrderForPlanBenefits = [];
      const draggedPlanBenefit = modifiedCategoryBenefits[source.index];
      modifiedCategoryBenefits.splice(source.index, 1);
      modifiedCategoryBenefits.splice(destination.index, 0, draggedPlanBenefit);
      // Get the minimum order value for the category benefits and increment the value
      // for each plan benefit.
      let minOrderValue = modifiedCategoryBenefits[0][0].order;
      modifiedCategoryBenefits.forEach((benefit) => {
        benefit.forEach((pb) => {
          if (pb.order < minOrderValue) {
            minOrderValue = pb.order;
          }
        });
      });
      let tempMinOrderValue = minOrderValue;
      minOrderValue += 1;
      // the first subcategory benefit in the list should have the minimum order value.
      modifiedCategoryBenefits.forEach((benefit) => {
        benefit.forEach((pb) => {
          if (pb.subcategory === initialSubCategory) {
            updatedOrderForPlanBenefits.push({ ...pb, order: tempMinOrderValue });
            tempMinOrderValue = minOrderValue + 1;
            minOrderValue = tempMinOrderValue;
          } else {
            updatedOrderForPlanBenefits.push({ ...pb, order: minOrderValue });
            minOrderValue += 1;
          }
        });
      });
      this.handleUpdateBenefitOrder(updatedOrderForPlanBenefits);
    }
  }

  handleReordering = () => {
    this.setState(state => ({
      enableReorder: !state.enableReorder,
    }));
  }

  handleResetButton = (categoryName) => {
    const { plan: { planBenefits } } = this.state;
    // Removing all the benefits that have the benefit id: undefined.
    const updatedPlanBenefits = planBenefits
      .filter(planBenefit => planBenefit.category === categoryName && planBenefit.id !== undefined);
    this.setState(state => ({
      ...state,
      plan: {
        ...state.plan,
        planBenefits: [
          ...state.plan.planBenefits.filter(pb => pb.category !== categoryName),
          ...updatedPlanBenefits,
        ],
      },
    }));
  }

  handleAddNewTable = () => {
    const { plan: { id: planId } } = this.state;
    const emptyPlanBenefit = [{
      id: undefined,
      name: '',
      value: '',
      category: '',
      subcategory: '',
      planId,
    }];
    this.setState(state => ({
      ...state,
      plan: {
        ...state.plan,
        planBenefits: [
          ...state.plan.planBenefits,
          ...emptyPlanBenefit,
        ],
      },
    }));
  }

  handleAddEmptyRow = (categoryName) => {
    const {
      plan: { id: planId, planBenefits },
    } = this.state;
    // Add a column by adding the first subcategory name.
    const subcategory = [...new Set(planBenefits
      .filter(planBenefit => planBenefit.category === categoryName)
      .map(planBenefit => planBenefit.subcategory))][0];
    const newBenefits = [{
      planId,
      name: '',
      category: categoryName,
      subcategory,
      value: '',
      id: undefined,
    }];
    this.setState(state => ({
      ...state,
      plan: {
        ...state.plan,
        planBenefits: [
          ...state.plan.planBenefits,
          ...newBenefits,
        ],
      },
    }));
  }

  handleAddEmptyColumn = (categoryName) => {
    const {
      plan: { id: planId, planBenefits },
    } = this.state;
    const benefitName = planBenefits
      .filter(planBenefit => planBenefit.category === categoryName)
      .map(planBenefit => planBenefit.name)[0]; // Add a column by adding the first benefit name.
    const newBenefits = [{
      planId,
      name: benefitName,
      category: categoryName,
      subcategory: '',
      value: '',
      id: undefined,
    }];
    this.setState(state => ({
      ...state,
      plan: {
        ...state.plan,
        planBenefits: [
          ...state.plan.planBenefits,
          ...newBenefits,
        ],
      },
    }));
  }

  render() {
    const {
      location: { search },
      match: { url: matchUrl, params: { carrierId, planId } },
      user,
      isRedesignFromState,
    } = this.props;
    const {
      editBenefitValue,
      editCategoryName,
      editSubCategoryName,
      editBenefitName,
      isDragged,
      loading,
      plan,
      enableReorder,
      addBenefit,
    } = this.state;
    const { query } = urlParse(search);
    const { frameless, shrinkFirstTable, isRedesign } = qs.parse(query);
    const isPageInFramelessMode = frameless !== undefined;
    const isRedesignQuery = isRedesign !== undefined && (isRedesign === 'True' || isRedesign === 'true');
    const benefitsSorted = (plan.planBenefits || [])
      .sort((a, b) => a.id - b.id)
      .sort((a, b) => a.order - b.order);
    const benefitsWithDefaults = (benefitsSorted || []).map(benefit => ({
      ...benefit,
      category: benefit.category || '',
      subcategory: benefit.subcategory || '',
    }));
    const categories = [...new Set(benefitsWithDefaults.map(b => b.category))]; // Unique categories
    const benefitsMarkup = categories.map((category, index) => {
      const benefitsInCategory = benefitsWithDefaults
        .filter(b => b.category === category);
      const editMode = !!user
                      && editCategoryName
                      && editCategoryName.value === category;
      let categoryContent;
      if (editMode) {
        categoryContent = (
          <TextEditor
            defaultValue={category}
            onCancel={() => this.handleCancelEdit()}
            onSave={e => this.handleSaveEdit(e)}
            autoSize
            rows={1}
          />
        );
      } else {
        categoryContent = category;
      }
      const handleGetContext = () => ({
        addBenefit,
        categoryName: category,
        categoryBenefits: benefitsInCategory,
        editAllowed: !!user,
        editBenefitValue,
        editSubCategoryName,
        editBenefitName,
        enableReorder,
        onStartEdit: this.handleStartEdit,
        onSaveEdit: this.handleSaveEdit,
        onCancelEdit: this.handleCancelEdit,
        onAddBenefit: this.handleAddPlanBenefit,
        onAddRow: this.handleAddEmptyRow,
        onAddColumn: this.handleAddEmptyColumn,
        width: shrinkFirstTable === undefined || index !== 0 ? '100%' : '60%',
        isPageInFramelessMode,
        onReset: this.handleResetButton,
      });
      return (
        <Draggable key={category} draggableId={`draggableBenefitTable-${category}`} index={index} class="category" isDragDisabled={!user || !enableReorder}>
          {provided => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
            >
              {(!isRedesignFromState && !isRedesignQuery) ? (
                <div>
                  <Legend
                    name="editCategoryName"
                    value={category}
                    editAvailable={!!user && !editMode}
                    onClick={editMode ? null : e => this.handleStartEdit(e)}
                  >
                    {categoryContent}
                  </Legend>
                  <Provider value={handleGetContext()}>
                    <BenefitCategory />
                  </Provider>
                </div>
              ) : (
                <div style={{ margin: '0px 25px' }}>
                  <LegendRedesign
                    name="editCategoryName"
                    value={category}
                    editAvailable={!!user && !editMode}
                    onClick={editMode ? null : e => this.handleStartEdit(e)}
                  >
                    {categoryContent}
                  </LegendRedesign>
                  <Provider value={handleGetContext()}>
                    <BenefitCategoryRedesign />
                  </Provider>
                </div>
              )
              }
            </div>
          )}
        </Draggable>
      );
    });

    const emptyMessage = 'This plan has no elements, something went wrong. Please try refreshing the page.';

    const reorderingButtonGroup = (
      <Box marginTop="4em" marginBottom="4em">
        <ButtonGroup>
          <SettingToggle
            action={{
              content: enableReorder ? 'Disable Reordering' : 'Enable Reordering',
              onAction: this.handleReordering,
            }}
            enabled={enableReorder}
          />
          {isDragged && (
            <Button primary onClick={() => this.handleUpdateBulkPlanBenefits(plan.planBenefits)}>
              Save New Order
            </Button>
          )}
        </ButtonGroup>
      </Box>
    );
    const planContents = (
      plan.planBenefits && plan.planBenefits.length === 0 ? emptyMessage : benefitsMarkup
    );
    const showAddTableButton = (plan.planBenefits || []).some(pb => pb.category === '' && pb.id === undefined && pb.subcategory === '');
    const addTableButton = !!user && (
      <ToolTip content="Fill the empty table before adding the new table." disabled={!showAddTableButton}>
        <Button onClick={() => this.handleAddNewTable()} disabled={showAddTableButton}>
          Add Table
        </Button>
      </ToolTip>
    );

    const draggableBenefitRows = (
      <DragDropContext onDragEnd={this.onDragEnd}>
        <Droppable droppableId={planId} type="category" isDropDisabled={!user || !enableReorder}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
            >
              {isPageInFramelessMode
                ? (
                  <>
                    { planContents }
                    {!snapshot.isDraggingOver && addTableButton }
                    {provided.placeholder}
                  </>
                )
                : (
                  <Card loading={loading}>
                    <Card.Block>
                      { /* TODO for every category, factor into component
                    * Take a look at https://www.w3.org/WAI/tutorials/tables/irregular/ for accessible irregular table */
                    }
                      {planContents}
                      {!snapshot.isDraggingOver && addTableButton }
                    </Card.Block>
                    {provided.placeholder}
                  </Card>
                )}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );

    const { page } = this.state;

    if (isPageInFramelessMode) {
      return (
        <React.Fragment>
          {draggableBenefitRows}
          {user ? <ExternalLink href={`/carriers/${carrierId}/plans/${planId}/edit`} target="_blank">Advanced Edit</ExternalLink> : undefined}
          {user && reorderingButtonGroup}
          <WindowEventListener event="resize" onEvent={this.sendHeightUpdate} />
        </React.Fragment>
      );
    }
    return (
      <Page
        breadcrumbs={user ? [{ content: 'Plans', href: `/carriers/${carrierId}/plans${page > 1 ? `?page=${page}` : ''}` }] : undefined}
        title={plan.name || 'Loading...'}
        subtitle={plan.cmsContractId && `${plan.cmsContractId}-${plan.cmsPbpId}-${plan.cmsSegmentId}, ${plan.cmsYear}`}
        primaryAction={user ? { content: 'Advanced Edit', href: `${matchUrl}/edit` } : undefined}
      >
        {draggableBenefitRows}
        {!user ? (
          <Box marginTop="4em" marginBottom="4em">
            <Text as="h3">Note: You must &quot;Log In&quot; to edit.</Text>
          </Box>
        ) : reorderingButtonGroup}
      </Page>
    );
  }
}

function mapStateToProps(state) {
  return {
    iFrameId: getIFrameId(state),
    theme: getTheme(state),
    user: getUserDetails(state),
    features: getFeatures(state),
    selectedLanguage: getSelectedLanguage(state),
    isRedesignFromState: getIsRedesign(state),
  };
}
ViewPlan.propTypes = propTypes;
ViewPlan.defaultProps = defaultProps;
export default connect(mapStateToProps)(ViewPlan);
